import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  LayoutDashboard,
  Users,
  FileText,
  MessageSquare,
  LogOut,
  Bell,
  DollarSign,
  Settings,
  BarChart3,
  Mail,
  Tag,
  BookOpen,
  Briefcase,
  ChevronDown,
  Shield,
  Gift,
  TrendingUp,
  Building2,
  Clock,
  PanelLeftClose,
  PanelLeft,
  Search,
  Menu,
  X,
  LinkIcon,
  PhoneCall,
  ScrollText,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

const navSections = (isSuperAdmin) => {
  const sections = [
    {
      items: [
        { name: "Dashboard", path: "/admin", icon: LayoutDashboard },
      ],
    },
    {
      label: "Clients & Projects",
      items: [
        { name: "Clients", path: "/admin/clients", icon: Users },
        { name: "Companies", path: "/admin/companies", icon: Building2 },
        { name: "Projects", path: "/admin/projects", icon: Briefcase },
      ],
    },
    {
      label: "Communication",
      items: [
        { name: "Chat", path: "/admin/chat", icon: MessageSquare },
        ...(isSuperAdmin ? [
          { name: "Email Templates", path: "/admin/email-templates", icon: Mail },
          { name: "Email Log", path: "/admin/email-logs", icon: ScrollText },
        ] : []),
      ],
    },
  ];

  if (isSuperAdmin) {
    sections.push(
      {
        label: "Financial",
        items: [
          { name: "Invoices", path: "/admin/invoices", icon: DollarSign, badge: "invoices" },
          { name: "Sales & Links", path: "/admin/payment-links", icon: LinkIcon },
          { name: "SDR Management", path: "/admin/sdr", icon: PhoneCall },
          { name: "Discounts", path: "/admin/discounts", icon: Tag },
        ],
      },
      {
        label: "Programs & Marketing",
        items: [
          { name: "Applications", path: "/admin/applications", icon: FileText },
          { name: "Contact Submissions", path: "/admin/contacts", icon: Mail },
          { name: "Referrals", path: "/admin/referrals", icon: Gift },
          { name: "Referral Analytics", path: "/admin/referral-analytics", icon: TrendingUp },
          { name: "Blog", path: "/admin/blog", icon: BookOpen },
        ],
      },
      {
        label: "Reports",
        items: [
          { name: "Consultant Hours", path: "/admin/consultant-hours", icon: Clock },
          { name: "Users", path: "/admin/users", icon: Users },
          { name: "Analytics", path: "/admin/analytics", icon: BarChart3 },
        ],
      }
    );
  }

  sections.push({
    items: [
      { name: "Preferences", path: "/admin/preferences", icon: Settings },
    ],
  });

  return sections;
};

const AdminLayout = ({ children }) => {
  const { user, token, logout, API, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const sections = navSections(isSuperAdmin);
    const expanded = {};
    sections.forEach((section) => {
      if (section.label) {
        const hasActive = section.items.some(
          (item) =>
            location.pathname === item.path ||
            (item.path !== "/admin" && location.pathname.startsWith(item.path))
        );
        if (hasActive) expanded[section.label] = true;
      }
    });
    setExpandedSections((prev) => ({ ...prev, ...expanded }));
  }, [location.pathname, isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isSuperAdmin]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const fetchNotifications = async () => {
    try {
      const [countRes, notifRes] = await Promise.all([
        fetch(`${API}/admin/notifications/count`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (countRes.ok) setNotificationCount((await countRes.json()).count);
      if (notifRes.ok) setNotifications((await notifRes.json()).slice(0, 5));
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const toggleSection = (label) => {
    setExpandedSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path) =>
    location.pathname === path ||
    (path !== "/admin" && location.pathname.startsWith(path));

  const sections = navSections(isSuperAdmin);

  const filteredSections = searchQuery
    ? sections
        .map((s) => ({
          ...s,
          items: s.items.filter((i) =>
            i.name.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((s) => s.items.length > 0)
    : sections;

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      {/* Brand-colored Top Bar */}
      <header className="bg-rnb-navy sticky top-0 z-50 shadow-md">
        <div className="flex items-center justify-between h-14 px-4 lg:px-6">
          {/* Left: Logo + collapse */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:block p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="sidebar-toggle"
            >
              {collapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/" className="flex items-center gap-2.5" data-testid="admin-logo">
              <div className="w-8 h-8 bg-white/15 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20">
                <span className="text-white font-bold text-sm">R</span>
              </div>
              {!collapsed && (
                <span className="font-heading font-bold text-white text-sm hidden sm:block tracking-wide">
                  RNB Consulting
                </span>
              )}
            </Link>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative rounded-lg w-9 h-9 p-0 text-white/70 hover:text-white hover:bg-white/10"
                    data-testid="notifications-btn"
                  >
                    <Bell className="h-[18px] w-[18px]" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-rnb-teal text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-bold">
                        {notificationCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0 shadow-lg border-gray-200" align="end">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-rnb-navy to-rnb-blue">
                    <p className="font-semibold text-sm text-white">Notifications</p>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notif) => (
                        <Link
                          key={notif.notification_id}
                          to="/admin/invoices"
                          className="flex items-start gap-3 p-3 hover:bg-gray-50 border-b border-gray-50 transition-colors"
                        >
                          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                            <DollarSign className="h-4 w-4 text-amber-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-rnb-navy">Invoice Pending</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {notif.created_by} &middot; {formatCurrency(notif.amount)} for {notif.client_name}
                            </p>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="py-8 text-center text-gray-400 text-sm">
                        No pending notifications
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* User info */}
            <div className="flex items-center gap-2.5 pl-3 ml-1 border-l border-white/20">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rnb-teal to-rnb-blue flex items-center justify-center text-white text-xs font-bold ring-2 ring-white/20">
                {user?.name?.charAt(0) || "A"}
              </div>
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-white leading-none">{user?.name || "Admin"}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {isSuperAdmin && <Shield className="h-3 w-3 text-rnb-teal" />}
                  <span className="text-[11px] text-white/50 capitalize">{user?.role?.replace("_", " ")}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="rounded-lg w-9 h-9 p-0 text-white/50 hover:text-white hover:bg-white/10"
                data-testid="admin-logout"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex relative">
        {/* Mobile overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
        )}

        {/* Mobile sidebar drawer */}
        <aside
          className={`fixed top-14 left-0 bottom-0 w-64 bg-white border-r border-gray-200/60 z-50 transform transition-transform duration-300 ease-out lg:hidden flex flex-col shadow-xl ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          data-testid="mobile-sidebar"
        >
          <div className="px-3 pt-3 pb-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input type="text" placeholder="Search menu..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg text-rnb-navy placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-rnb-blue/30"
              />
            </div>
          </div>
          <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
            {filteredSections.map((section, si) => (
              <div key={section.label || `section-mobile-${si}`}>
                {section.label && (
                  <button className="w-full flex items-center justify-between px-3 py-2 mt-3 first:mt-0 text-[11px] font-semibold text-gray-400 uppercase tracking-wider hover:text-rnb-blue transition-colors" onClick={() => toggleSection(section.label)}>
                    {section.label}
                    <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${expandedSections[section.label] === false ? "-rotate-90" : ""}`} />
                  </button>
                )}
                <div className={`space-y-0.5 ${section.label && expandedSections[section.label] === false ? "hidden" : ""}`}>
                  {section.items.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <Link key={item.path} to={item.path} className={`flex items-center gap-2.5 rounded-lg px-3 py-2 transition-all duration-150 ${active ? "bg-gradient-to-r from-rnb-navy to-rnb-blue text-white shadow-sm" : "text-gray-600 hover:bg-rnb-light hover:text-rnb-navy"}`}>
                        <item.icon className={`shrink-0 w-4 h-4 ${active ? "text-white" : "text-gray-400"}`} />
                        <span className="text-[13px] font-medium truncate">{item.name}</span>
                        {item.badge === "invoices" && notificationCount > 0 && <Badge className="ml-auto bg-rnb-teal text-white text-[10px] px-1.5 py-0 h-4 rounded-full">{notificationCount}</Badge>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Desktop Sidebar */}
        <aside
          className={`${
            collapsed ? "w-[68px]" : "w-60"
          } bg-white border-r border-gray-200/60 min-h-[calc(100vh-56px)] sticky top-14 transition-all duration-300 hidden lg:flex flex-col shadow-sm`}
          data-testid="admin-sidebar"
        >
          {!collapsed && (
            <div className="px-3 pt-3 pb-1">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg text-rnb-navy placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-rnb-blue/30 focus:border-rnb-blue/30"
                  data-testid="sidebar-search"
                />
              </div>
            </div>
          )}

          <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
            {filteredSections.map((section, si) => (
              <div key={section.label || `section-mobile-${si}`}>
                {section.label && !collapsed && (
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 mt-3 first:mt-0 text-[11px] font-semibold text-gray-400 uppercase tracking-wider hover:text-rnb-blue transition-colors"
                    onClick={() => toggleSection(section.label)}
                    data-testid={`nav-section-${section.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {section.label}
                    <ChevronDown
                      className={`w-3 h-3 transition-transform duration-200 ${
                        expandedSections[section.label] === false ? "-rotate-90" : ""
                      }`}
                    />
                  </button>
                )}

                {section.label && collapsed && (
                  <div className="mx-3 my-2 border-t border-gray-100" />
                )}

                <div
                  className={`space-y-0.5 ${
                    section.label && !collapsed && expandedSections[section.label] === false
                      ? "hidden"
                      : ""
                  }`}
                >
                  {section.items.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-2.5 rounded-lg transition-all duration-150 group relative ${
                          collapsed ? "justify-center px-0 py-2.5 mx-1" : "px-3 py-2"
                        } ${
                          active
                            ? "bg-gradient-to-r from-rnb-navy to-rnb-blue text-white shadow-sm"
                            : "text-gray-600 hover:bg-rnb-light hover:text-rnb-navy"
                        }`}
                        data-testid={`admin-nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                        title={collapsed ? item.name : undefined}
                      >
                        <item.icon
                          className={`shrink-0 ${collapsed ? "w-[18px] h-[18px]" : "w-4 h-4"} ${
                            active ? "text-white" : "text-gray-400 group-hover:text-rnb-blue"
                          }`}
                        />
                        {!collapsed && (
                          <span className="text-[13px] font-medium truncate">{item.name}</span>
                        )}
                        {item.badge === "invoices" && notificationCount > 0 && !collapsed && (
                          <Badge className="ml-auto bg-rnb-teal text-white text-[10px] px-1.5 py-0 h-4 rounded-full">
                            {notificationCount}
                          </Badge>
                        )}
                        {collapsed && (
                          <div className="absolute left-full ml-2 px-2 py-1 bg-rnb-navy text-white text-xs rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                            {item.name}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
